import { t } from "i18next";
import type {
  AlarmDto,
  AdvancedAlarmEnum,
  AdvancedAlarmItemDto,
  AdvancedAlarmLogicalOperatorEnum,
  AlarmUpdateDto,
  DayOfWeek,
  eAlertType,
  ePriority,
} from "~/types/api";
import type { AlarmFormType } from "~/types/ui";

// Simple alarms bind a single tag/tagDataKey directly on the alarm. Advanced
// alarms bind one per condition in `advancedAlarms` instead, so `tag` and
// `tagDataKey` are always null on those - resolve the display value from the
// conditions so the table doesn't show N/A for alarms that do have data.
export function getAlarmTagDisplay(alarm: AlarmDto): string | null {
  if (!alarm.advanced) return alarm.tag?.name ?? null;

  const items = alarm.advancedAlarms ?? [];
  if (items.length === 0) return null;

  const uniqueTagIds = new Set(items.map((item) => item.device?.deveui));
  return uniqueTagIds.size === 1
    ? (items[0].device?.name ?? null)
    : t("common.various");
}

export function getAlarmTagDataKeyDisplay(alarm: AlarmDto): string | null {
  if (!alarm.advanced) return alarm.tagDataKey ?? null;

  const items = alarm.advancedAlarms ?? [];
  if (items.length === 0) return null;

  const uniqueKeys = new Set(items.map((item) => item.device?.key));
  return uniqueKeys.size === 1
    ? (items[0].device?.key ?? null)
    : t("common.various");
}

export const PriorityEnum = {
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2,
} as const;

export const AlertTypeEnum = {
  EMAIL_SMS_LOG: 0,
  SMS_LOG: 1,
  EMAIL_LOG: 2,
  LOG_ONLY: 3,
} as const;

export const DayOfWeekEnum = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
} as const;

export function priorityToString(priority: ePriority): string {
  return t(`alarms.priority.${priority}`);
}

export function alertTypeToString(alertType: eAlertType): string {
  return t(`alarms.alertType.${alertType}`);
}

export function dayOfWeekToString(day: DayOfWeek): string {
  return t(`alarms.days.${day}`);
}

export type AlarmStatus = "inactive" | "triggered" | "flagging" | "ok";

export const AlarmStatusColorMap: Record<AlarmStatus, string> = {
  inactive: "text-gray-400",
  triggered: "text-red-600",
  flagging: "text-yellow-600",
  ok: "text-green-600",
};

export function getAlarmStatus(alarm: AlarmDto): AlarmStatus {
  if (!alarm.active) return "inactive";
  if (alarm.activeAlarms && alarm.activeAlarms.length > 0) return "triggered";
  if (alarm.flagging) return "flagging";
  return "ok";
}

export function alarmStatusToString(status: AlarmStatus): string {
  return t(`alarms.status.${status}`);
}

export const SignalTypeEnum = {
  ANALOG: 0,
  DIGITAL: 1,
} as const;

export const DigitalTypeEnum = {
  TRIGGER_HIGH: 0,
  TRIGGER_LOW: 1,
} as const;

export type DigitalTriggerLimits = {
  limitHigh: number | null;
  limitLow: number | null;
};

// Digital triggers are encoded onto the analog limitHigh/limitLow pair rather than
// having their own backend fields: TRIGGER_HIGH <-> {limitHigh: 0, limitLow: null},
// TRIGGER_LOW <-> {limitHigh: null, limitLow: 1}.
export function getLimitsFromDigitalType(
  digitalType: number,
): DigitalTriggerLimits {
  return digitalType === DigitalTypeEnum.TRIGGER_LOW
    ? { limitHigh: null, limitLow: 1 }
    : { limitHigh: 0, limitLow: null };
}

export function getSignalTypeFromLimits(
  limitHigh?: number | null,
  limitLow?: number | null,
): { signalType: number; digitalType: number | null } {
  if (limitLow === null && limitHigh === 0) {
    return {
      signalType: SignalTypeEnum.DIGITAL,
      digitalType: DigitalTypeEnum.TRIGGER_HIGH,
    };
  }
  if (limitHigh === null && limitLow === 1) {
    return {
      signalType: SignalTypeEnum.DIGITAL,
      digitalType: DigitalTypeEnum.TRIGGER_LOW,
    };
  }
  return { signalType: SignalTypeEnum.ANALOG, digitalType: null };
}

export const LogicalOperatorEnum = {
  OR: 0,
  AND: 1,
} as const;

export function logicalOperatorToString(
  op: AdvancedAlarmLogicalOperatorEnum,
): string {
  return t(`alarms.conditions.logicalOperator.${op}`);
}

// The LHS of an advanced condition is always a sensor reference (ConditionTree.vue's
// device picker only ever offers AdvancedAlarmEnum.SENSORVALUE), so it is not stored
// per-row; the RHS carries the only meaningful `type`.
export const AdvancedAlarmValueTypeEnum = {
  SENSORVALUE: 0,
  TEXT: 1,
  NUMBER: 2,
  DATE: 3,
  EMPTY: 4,
} as const;

export function advancedAlarmValueTypeToString(type: AdvancedAlarmEnum): string {
  return t(`alarms.conditions.valueTypes.${type}`);
}

export const conditionOperators = ["<", "=", ">", "<=", ">=", "!="] as const;

export function defaultAlarmCondition(index: number): AdvancedAlarmItemDto {
  return {
    index,
    op: ">",
    value: { type: AdvancedAlarmValueTypeEnum.TEXT, value: "" },
  };
}

export function reindexAlarmConditions(
  items: AdvancedAlarmItemDto[],
): AdvancedAlarmItemDto[] {
  return items.map((item, index) => ({ ...item, index }));
}

export function validateAlarmConditions(items: AdvancedAlarmItemDto[]): boolean {
  if (!items || items.length === 0) return false;
  return items.every(
    (item) =>
      !!item.op &&
      !!item.device?.deveui &&
      !!item.device?.key &&
      (item.value?.type === AdvancedAlarmValueTypeEnum.EMPTY ||
        !!item.value?.value),
  );
}

// NUMBER form fields hand their value back as a string, and as "" once cleared.
// A blank limit has to stay null rather than collapse to 0: 0 and null are
// distinct values in the digital-trigger encoding above.
function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
}

export function toAlarmFormType(alarm: AlarmDto): AlarmFormType {
  return {
    alarmId: alarm.alarmId,
    name: alarm.name ?? "",
    message: alarm.message ?? "",
    activeDays: alarm.activeDays?.map((d) => d.day as DayOfWeek) ?? [
      1, 2, 3, 4, 5,
    ],
    contacts:
      alarm.alarmContacts
        ?.map((c) => c.user?.userId)
        .filter((id): id is number => id != null) ?? [],
    advanced: alarm.advanced ?? false,
    tagDeveui: alarm.tag?.deveui ?? "",
    tagDataKey: alarm.tagDataKey ?? "",
    limitLow: alarm.limitLow ?? null,
    limitHigh: alarm.limitHigh ?? null,
    alarmDelay: alarm.alarmDelay ?? 10,
    priority: alarm.priority ?? PriorityEnum.HIGH,
    alertType: alarm.alertType ?? AlertTypeEnum.EMAIL_SMS_LOG,
    autoAcknowledge: alarm.autoAcknowledge ?? false,
    active: alarm.active ?? true,
    logicalOperator: alarm.logicalOperator ?? LogicalOperatorEnum.OR,
    alarmConditions: alarm.advancedAlarms ?? [{ index: 0 }],
  };
}

export function toAlarmPayload(data: AlarmFormType): AlarmUpdateDto {
  return {
    alarmId: toNullableNumber(data.alarmId) ?? 0,
    name: data.name,
    message: data.message,
    activeDays: data.activeDays?.map((day) => ({ day })) ?? [],
    contacts: data.contacts,
    advanced: data.advanced,
    tagDeveui: data.tagDeveui,
    tagDataKey: data.tagDataKey,
    limitLow: toNullableNumber(data.limitLow),
    limitHigh: toNullableNumber(data.limitHigh),
    alarmDelay: data.alarmDelay,
    priority: data.priority,
    alertType: data.alertType,
    autoAcknowledge: data.autoAcknowledge,
    active: data.active,
    logicalOperator: data.logicalOperator,
    alarmConditions: data.alarmConditions,
  };
}
